import type { CellContext } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useState } from 'react';
import Input from '@/components/UI/Form/Input';

interface TableMeta {
  updateTableData?: (index: number, id: string, value: string | any) => void;
  editableRowIndex?: number | null;
  setEditableRowIndex?: (index: number | null) => void;
}

const EditableCell = (props: CellContext<any, any>) => {
  const {
    getValue,
    row: { index },
    column: { id },
    table,
  } = props;

  const meta = table.options.meta as TableMeta | undefined;
  const updateTableData = meta?.updateTableData;
  const editableRowIndex = meta?.editableRowIndex;

  const initialValue = getValue();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState('');

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (e.target.name === 'name' && newValue.length > 25) {
      setError('This must be less than 25 characters');
    } else {
      setError('');
      updateTableData?.(index, id, newValue);
    }
  };

  // We'll only update the external data when the input is blurred
  const _onBlur = () => {
    updateTableData?.(index, id, value);
  };

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // For non-editable cells, use getValue() directly to ensure we have the latest value
  const displayValue = index === editableRowIndex ? value : (getValue() ?? '');

  return index === editableRowIndex ? (
    <>
      <Input type='text' name={id} value={value || ''} placeholder='e.g. Sage Office' changed={onChange} required />
      {error && <p className='text-tertiaryColor-normal'>{error}</p>}
    </>
  ) : (
    <p>{displayValue}</p>
  );
};

export default EditableCell;
